/**
 * `/dm/docs` address contract: the filter (`kind`, `audience`, `status` — the doors' own axes),
 * the search text (`q`) and the open doc (`?doc=<key>`, the get tool's `key` arm). Unknown
 * values are dropped and the address is repaired to what was applied; keys this page does not
 * own (the shell's `help`) are kept (the `dmPoliciesUrl` rule).
 */
import {
  DM_DOC_AUDIENCE_VALUES,
  DM_DOC_KIND_VALUES,
  DM_DOC_STATUS_VALUES,
  DM_DOCS_PATH,
  DM_DOCS_SEARCH_DEFAULT_STATUS,
  DM_DOCS_SEARCH_PATH,
  dmDocsQueryProblem,
  isDmDocKey,
  type DmDocAudience,
  type DmDocKind,
  type DmDocStatus,
} from './dmDocWire.js';

export const DM_DOCS_ROUTE = '/dm/docs';

export interface DmDocsFilter {
  kind: DmDocKind | null;
  audience: DmDocAudience | null;
  status: DmDocStatus | null;
  /** Search text, trimmed; `null` lists instead of searching. */
  q: string | null;
}

export const DM_DOCS_FILTER_AXES = ['kind', 'audience', 'status', 'q'] as const;
export const DM_DOC_SELECTED_KEY = 'doc';

function memberOf<T extends string>(raw: string | null, values: readonly T[]): T | null {
  return raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

export function parseDmDocsSearch(search: URLSearchParams): DmDocsFilter {
  const q = search.get('q');
  return {
    kind: memberOf(search.get('kind'), DM_DOC_KIND_VALUES),
    audience: memberOf(search.get('audience'), DM_DOC_AUDIENCE_VALUES),
    status: memberOf(search.get('status'), DM_DOC_STATUS_VALUES),
    q: q !== null && dmDocsQueryProblem(q) === null ? q.trim() : null,
  };
}

export function parseDmDocSelected(search: URLSearchParams): string | null {
  const raw = search.get(DM_DOC_SELECTED_KEY);
  return raw !== null && isDmDocKey(raw) ? raw : null;
}

/** Writes the filter onto `base`; keys this page does not own are kept. */
export function serializeDmDocsSearch(
  filter: DmDocsFilter,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const axis of DM_DOCS_FILTER_AXES) {
    const value = filter[axis];
    if (value === null) next.delete(axis);
    else next.set(axis, value);
  }
  return next;
}

export function withDmDocSelected(base: URLSearchParams, key: string | null): URLSearchParams {
  const next = new URLSearchParams(base);
  if (key === null) next.delete(DM_DOC_SELECTED_KEY);
  else next.set(DM_DOC_SELECTED_KEY, key);
  return next;
}

/** The repaired address, or `null` when it is already canonical. */
export function canonicalDmDocsSearch(search: URLSearchParams): string | null {
  const canonical = withDmDocSelected(
    serializeDmDocsSearch(parseDmDocsSearch(search), search),
    parseDmDocSelected(search),
  ).toString();
  return canonical === search.toString() ? null : canonical;
}

/** `/dm/docs?q=<text>` — the address a search link carries (the help drawer's). */
export function dmDocsSearchHref(q: string): string {
  return `${DM_DOCS_ROUTE}?${new URLSearchParams({ q }).toString()}`;
}

/** `/dm/docs?doc=<key>` — one doc's address. */
export function dmDocHref(key: string): string {
  return `${DM_DOCS_ROUTE}?${new URLSearchParams({ [DM_DOC_SELECTED_KEY]: key }).toString()}`;
}

export interface DmDocsRead {
  path: string;
  params: Record<string, string | number>;
}

/**
 * The door a filter reads: the list door, or the search door when there is text. A `null` axis
 * is omitted (both query schemas are strict); search sends its status explicitly — the search
 * door's own default, which the page discloses — so the request and the disclosure read one value.
 */
export function dmDocsRead(filter: DmDocsFilter, limit: number, offset: number): DmDocsRead {
  const params: Record<string, string | number> = {};
  if (filter.kind !== null) params.kind = filter.kind;
  if (filter.audience !== null) params.audience = filter.audience;
  if (filter.q === null) {
    if (filter.status !== null) params.status = filter.status;
    return { path: DM_DOCS_PATH, params: { ...params, limit, offset } };
  }
  return {
    path: DM_DOCS_SEARCH_PATH,
    params: { query: filter.q, status: dmDocsSearchStatus(filter), ...params, limit, offset },
  };
}

/** The status a search reads: the chosen one, else the door's default. */
export function dmDocsSearchStatus(filter: DmDocsFilter): DmDocStatus {
  return filter.status ?? DM_DOCS_SEARCH_DEFAULT_STATUS;
}
